// src/pages/PrivacyPolicy.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowLeft } from 'lucide-react';

const sections = [
  { id: 1, title: '第1条（個人情報の収集について）', content: '当社は、本サービスの提供にあたり、以下の個人情報を収集することがあります。\n\n・氏名\n・メールアドレス\n・電話番号\n・クレジットカード情報（決済代行業者を通じて処理されます）\n・診断結果・学習記録等のサービス利用データ\n・IPアドレス、クッキー、ブラウザ情報等のアクセス情報' },
  { id: 2, title: '第2条（個人情報の利用目的）', content: '当社は、収集した個人情報を以下の目的に利用します。\n\n・本サービスの提供・運営・改善\n・ユーザー登録の受付、本人確認\n・利用料金の請求・決済処理\n・サービスに関するお知らせ・メールマガジンの配信\n・ユーザーサポートへの対応\n・本サービスの利用状況の分析・統計処理' },
  { id: 3, title: '第3条（個人情報の第三者提供）', content: '当社は、次の場合を除き、個人情報を第三者に提供しません。\n\n・ユーザーご本人の同意がある場合\n・法令に基づく場合\n・人の生命・身体・財産の保護のために必要がある場合\n・国の機関もしくは地方公共団体が法令の定める事務を遂行することに対して協力する必要がある場合' },
  { id: 4, title: '第4条（個人情報の安全管理）', content: '当社は、個人情報の紛失、破壊、改ざん及び漏洩などのリスクに対して、個人情報の安全管理が図られるよう、必要かつ適切な措置を講じます。\n\n本サービスではGoogle Firebase（Firebase Authentication、Cloud Firestore）を使用しており、データはGoogleのセキュリティ基準に基づき保護されています。決済情報はUnivaPayを通じて処理され、当社はクレジットカード番号を保持しません。' },
  { id: 5, title: '第5条（クッキー等の使用について）', content: '本サービスでは、ユーザーの利便性向上のためにクッキー（Cookie）を使用しています。クッキーは、ウェブブラウザを通じてユーザーのコンピュータに保存される小さなデータです。\n\nユーザーはブラウザの設定によりクッキーの使用を拒否することができますが、一部の機能が正常に動作しない場合があります。\n\nGoogle Analytics等のアクセス解析ツールを使用して、本サービスの利用状況を分析する場合があります。' },
  { id: 6, title: '第6条（個人情報の開示・訂正・削除）', content: 'ユーザーは、当社が保有するご自身の個人情報について、開示、訂正、追加、削除、利用停止を求めることができます。\n\nご希望の場合は、お問い合わせ窓口（tominagacccyuki@gmail.com）までご連絡ください。ご本人確認の上、合理的な期間内に対応いたします。' },
  { id: 7, title: '第7条（プライバシーポリシーの変更）', content: '当社は、必要に応じて、本プライバシーポリシーを変更することがあります。変更後のプライバシーポリシーは、本サービス上に掲載した時点から効力を生じるものとします。\n\n重要な変更がある場合は、本サービス上での告知またはメール等によりユーザーに通知します。' },
  { id: 8, title: '第8条（お問い合わせ窓口）', content: '本プライバシーポリシーに関するお問い合わせは、以下の窓口までお願いいたします。\n\n事業者名：富永コーチングカントリークラブ株式会社\nサービス名：NOBISHIROコンピテンシー®\nメールアドレス：tominagacccyuki@gmail.com' },
];

const AccordionItem = ({ section, isOpen, onToggle }) => (
  <div className="border-b border-white/10">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 px-6 text-left hover:bg-white/5 transition-colors group">
      <span className="text-sm font-medium text-white/90 group-hover:text-amber-400 transition-colors">{section.title}</span>
      <ChevronDown className={`w-4 h-4 text-amber-400 flex-shrink-0 ml-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
      <div className="px-6 pb-5"><p className="text-sm text-white/60 leading-relaxed whitespace-pre-line">{section.content}</p></div>
    </div>
  </div>
);

const PrivacyPolicy = () => {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"><ArrowLeft className="w-4 h-4" />ダッシュボードに戻る</Link>
          <span className="text-xs text-amber-400 font-medium tracking-widest uppercase">Legal</span>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-400/50" />
            <span className="text-xs text-amber-400 tracking-widest uppercase font-medium">Privacy Policy</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-400/50" />
          </div>
          <h1 className="text-3xl font-bold text-center mb-3">プライバシーポリシー</h1>
          <p className="text-center text-white/40 text-sm">最終更新日：2026年3月8日</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <p className="text-sm text-white/70 leading-relaxed">富永コーチングカントリークラブ株式会社（以下「当社」）は、「NOBISHIROコンピテンシー®」において取得するユーザーの個人情報の取り扱いについて、以下のとおりプライバシーポリシーを定めます。</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden mb-12">
          {sections.map((section) => (<AccordionItem key={section.id} section={section} isOpen={openId === section.id} onToggle={() => setOpenId(openId === section.id ? null : section.id)} />))}
        </div>
        <div className="border border-amber-400/20 rounded-xl p-6 bg-amber-400/5">
          <h3 className="text-sm font-semibold text-amber-400 mb-4 tracking-wider uppercase">お問い合わせ窓口</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex gap-4"><dt className="text-white/40 w-24 flex-shrink-0">事業者名</dt><dd className="text-white/80">富永コーチングカントリークラブ株式会社</dd></div>
            <div className="flex gap-4"><dt className="text-white/40 w-24 flex-shrink-0">サービス名</dt><dd className="text-white/80">NOBISHIROコンピテンシー®</dd></div>
            <div className="flex gap-4"><dt className="text-white/40 w-24 flex-shrink-0">メール</dt><dd className="text-white/80">tominagacccyuki@gmail.com</dd></div>
          </dl>
        </div>
        <div className="mt-12 text-center">
          <Link to="/terms" className="text-xs text-amber-400 hover:underline mr-6">利用規約</Link>
          <Link to="/dashboard" className="text-xs text-white/40 hover:text-white transition-colors">ダッシュボードに戻る</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
